import { Dialog, Notify } from 'quasar';
import MessageValidationDialog from './MessageValidationDialog.vue';
import TicketCreateReviewDialog from './TicketCreateReviewDialog.vue';
import ValidationErrorDialog from './ValidationErrorDialog.vue';
import { useTicketsComposable } from './useTicketsComposable';
import {
  MAX_VALIDATION_TEXT_LENGTH,
  MessageGateAction,
  ValidationKind,
  type MessageReviewSection,
  type ValidateClientTextResult,
} from './ticketsTypes.ts';

const TOO_LONG_MESSAGE =
  'The text is too long to check automatically — it will be sent without the AI check.';

const notifyTooLong = (): void => {
  Notify.create({ message: TOO_LONG_MESSAGE, color: 'warning', position: 'top' });
};

export type ValidationGateResult =
  | { action: MessageGateAction.Send; text: string }
  | { action: MessageGateAction.Edit; text: string }
  | { action: MessageGateAction.Cancel };

export type CreateReviewResult =
  | { action: MessageGateAction.Send; subject: string; message: string }
  | { action: MessageGateAction.Cancel };

/**
 * Advisory "review before sending" gates for accountant text.
 *
 * - `runValidationGate` — a single chat message; never blocks, fails open.
 * - `runCreateReviewGate` — subject + message together in one modal for ticket creation.
 */
type ErrorChoice = 'retry' | 'send' | 'cancel';

export const useMessageValidation = () => {
  const { validateClientText, validateTicketDraft } = useTicketsComposable();

  // On AI failure, ask the accountant what to do instead of silently sending.
  const openErrorDialog = (sendLabel: string): Promise<ErrorChoice> =>
    new Promise((resolve) => {
      let settled = false;
      const finish = (r: ErrorChoice) => {
        if (settled) return;
        settled = true;
        resolve(r);
      };

      Dialog.create({
        component: ValidationErrorDialog,
        componentProps: { sendLabel },
      })
        .onOk((payload: { action: 'retry' | 'send' }) =>
          finish(payload?.action === 'retry' ? 'retry' : 'send'),
        )
        .onCancel(() => finish('cancel'))
        .onDismiss(() => finish('cancel'));
    });

  const openDialog = (
    result: ValidateClientTextResult,
    original: string,
  ): Promise<ValidationGateResult> =>
    new Promise((resolve) => {
      let settled = false;
      const finish = (r: ValidationGateResult) => {
        if (settled) return;
        settled = true;
        resolve(r);
      };

      Dialog.create({
        component: MessageValidationDialog,
        componentProps: {
          original,
          improvedEn: result.improved_en,
          translation: result.translation,
          translationLanguage: result.translation_language,
          issues: result.issues,
          foreignFragments: result.foreign_fragments ?? [],
        },
      })
        .onOk((payload: { action: MessageGateAction; text: string }) =>
          finish(
            payload?.action === MessageGateAction.Edit
              ? { action: MessageGateAction.Edit, text: payload.text ?? original }
              : { action: MessageGateAction.Send, text: payload?.text ?? original },
          ),
        )
        .onCancel(() => finish({ action: MessageGateAction.Cancel }))
        // Backdrop / ESC dismiss resolves last; guard keeps the OK/Cancel choice.
        .onDismiss(() => finish({ action: MessageGateAction.Cancel }));
    });

  const runValidationGate = async (rawText: string): Promise<ValidationGateResult> => {
    const text = rawText?.trim() ?? '';
    // Nothing to validate (e.g. files-only message) — let it through.
    if (!text) return { action: MessageGateAction.Send, text: rawText };

    // Too long to check reliably → skip the check and tell the accountant why.
    if (text.length > MAX_VALIDATION_TEXT_LENGTH) {
      notifyTooLong();
      return { action: MessageGateAction.Send, text: rawText };
    }

    // Retry loop: on AI error, let the accountant retry, send anyway, or cancel.
    for (;;) {
      let result: ValidateClientTextResult | undefined;
      try {
        result = await validateClientText(text, ValidationKind.Message);
      } catch {
        const choice = await openErrorDialog('Send anyway');
        if (choice === 'retry') continue;
        return choice === 'send'
          ? { action: MessageGateAction.Send, text: rawText }
          : { action: MessageGateAction.Cancel };
      }

      // Clean draft → send the original as typed, no dialog.
      if (!result || !result.needs_improvement) {
        return { action: MessageGateAction.Send, text: rawText };
      }

      return openDialog(result, rawText);
    }
  };

  const openCreateDialog = (
    sections: MessageReviewSection[],
    subjectText: string,
    messageText: string,
  ): Promise<CreateReviewResult> =>
    new Promise((resolve) => {
      let settled = false;
      const finish = (r: CreateReviewResult) => {
        if (settled) return;
        settled = true;
        resolve(r);
      };

      Dialog.create({
        component: TicketCreateReviewDialog,
        componentProps: { sections },
      })
        .onOk((payload: { texts: Record<string, string> }) =>
          finish({
            action: MessageGateAction.Send,
            // Fields not shown (no improvement needed) keep their original text.
            subject: payload?.texts?.subject ?? subjectText,
            message: payload?.texts?.message ?? messageText,
          }),
        )
        .onCancel(() => finish({ action: MessageGateAction.Cancel }))
        .onDismiss(() => finish({ action: MessageGateAction.Cancel }));
    });

  const runCreateReviewGate = async (
    subjectText: string,
    messageText: string,
  ): Promise<CreateReviewResult> => {
    const subject = subjectText?.trim() ?? '';
    const message = messageText?.trim() ?? '';

    // Nothing to validate → create with the originals.
    if (!subject && !message) {
      return { action: MessageGateAction.Send, subject: subjectText, message: messageText };
    }

    // Too long to check reliably → skip the check and tell the accountant why.
    if (subject.length > MAX_VALIDATION_TEXT_LENGTH || message.length > MAX_VALIDATION_TEXT_LENGTH) {
      notifyTooLong();
      return { action: MessageGateAction.Send, subject: subjectText, message: messageText };
    }

    // Retry loop: on AI error, let the accountant retry, create anyway, or cancel.
    // The backend validates message + subject together (subject uses the cleaned
    // message as context), so this is a single call.
    for (;;) {
      let result;
      try {
        result = await validateTicketDraft(subjectText, messageText);
      } catch {
        const choice = await openErrorDialog('Create anyway');
        if (choice === 'retry') continue;
        return choice === 'send'
          ? { action: MessageGateAction.Send, subject: subjectText, message: messageText }
          : { action: MessageGateAction.Cancel };
      }

      if (!result) {
        return { action: MessageGateAction.Send, subject: subjectText, message: messageText };
      }

      const sections: MessageReviewSection[] = [];
      if (subject && result.subject?.needs_improvement) {
        sections.push({
          key: 'subject',
          label: 'Subject',
          original: subjectText,
          improvedEn: result.subject.improved_en,
          translation: result.subject.translation,
          translationLanguage: result.subject.translation_language,
          issues: result.subject.issues,
          foreignFragments: result.subject.foreign_fragments ?? [],
        });
      }
      if (message && result.message?.needs_improvement) {
        sections.push({
          key: 'message',
          label: 'Message',
          original: messageText,
          improvedEn: result.message.improved_en,
          translation: result.message.translation,
          translationLanguage: result.message.translation_language,
          issues: result.message.issues,
          foreignFragments: result.message.foreign_fragments ?? [],
        });
      }

      // Nothing to improve → proceed with the originals, no dialog.
      if (!sections.length) {
        return { action: MessageGateAction.Send, subject: subjectText, message: messageText };
      }

      return openCreateDialog(sections, subjectText, messageText);
    }
  };

  return { runValidationGate, runCreateReviewGate };
};
